
import { Component, Vue, Watch } from 'vue-property-decorator'
import AdminDomainForm from '@/components/admin/AdminDomainForm.vue'
import AdminWorkspacesFromDomains from '@/components/admin/AdminWorkspacesFromDomains.vue'
import ImportFile from '@/components/generic/ImportFile.vue'
import catalogsService from '@/services/CatalogsService'
import { CrudActions, CatalogGridItemColumn, DomainCatalogFilters, Domain, DomainCatalogGridItem, Client, Workspace } from '@/types/Catalogs'
import DateSelector from '@/components/generic/DateSelector.vue'
import TopToolBarPaginator from '@/components/generic/TopToolBarPaginator.vue'
import { PageRequest } from '@/types/Support'
import userInfoStore from '@/store/userInfo'
import { PermissionNames, RoleNames } from '@/types/Auth'
import moment from 'moment'
import reportsService from '@/services/ReportsService'
import { ErrorImport, ImportErrorCode } from '@/types/Errors'
import { ErrorTitles, ImportationErrorAdvices } from '@/types/ErrorDictionary'
import DateFormat from '@/services/DateFormat'
import LogService, { ToString } from '@/services/support/LogService'

@Component({
  components: {
    AdminDomainForm,
    AdminWorkspacesFromDomains,
    ImportFile,
    DateSelector,
    TopToolBarPaginator
  }
})
export default class AdminDomains extends Vue {
  PAGE_SIZE = 500
  PAGE_SIZE_VISIBLE_ITEMS = 50
  itemsVisibleRange = [0, this.PAGE_SIZE_VISIBLE_ITEMS]
  paginatorKey = 0
  crudActions = CrudActions
  currentAction = CrudActions.READ
  loadingData = false
  leftSidebarCollapsed = false
  clients: any[] = []
  workspaces: any = {}
  domainWorkspaces: any[] = []
  // Grid columns definition
  columns: CatalogGridItemColumn[] = []
  items: DomainCatalogGridItem[] = []
  filteredItems: DomainCatalogGridItem[] = []
  filters = new DomainCatalogFilters()
  selectedItem = new DomainCatalogGridItem()
  importDomainFields = [
    { key: 'id', label: 'ID' },
    { key: 'name', label: 'Nombre' }
  ]
  openCloseDomainConfirmation: boolean = false
  openCloseDomainLoading: boolean = false

  beforeMount () {
    this.columns = [
      CatalogGridItemColumn.def('name', 'Nombre', true),
      CatalogGridItemColumn.def('id', 'ID', true),
      CatalogGridItemColumn.def('status', 'Estado (Workspace dueño)', true),
      CatalogGridItemColumn.def('ownerDomain', 'Dueño del Dominio')
    ]
    if (this.isFinancesRole) {
      this.columns = this.columns.concat([
        CatalogGridItemColumn.def('domainTypeName', 'Categoría de cliente'),
        CatalogGridItemColumn.def('productLine', 'Línea de producto'),
        CatalogGridItemColumn.def('businessSegment', 'Segmento de negocio'),
        CatalogGridItemColumn.def('billingCode', 'Código de facturación')
      ])
    } else if (this.isOperationRole) {
      this.columns = this.columns.concat([
        CatalogGridItemColumn.def('securityLevelName', 'Nivel de seguridad', true),
        CatalogGridItemColumn.def('webBMFMinimumRevision', 'Versión de webbmf mínima'),
        CatalogGridItemColumn.def('broadcast', 'Broadcast'),
        CatalogGridItemColumn.def('usePredictiveDialerOpt', 'Uses predictive dialer options'),
        CatalogGridItemColumn.def('limitLicenses', 'Limite de licencias'),
        CatalogGridItemColumn.def('mapProvider', 'Proveedor de mapas'),
        CatalogGridItemColumn.def('fullTextSearchType', 'Proveedor de búsqueda avanzada'),
        CatalogGridItemColumn.def('trackingMode', 'Tracking mode'),
        CatalogGridItemColumn.def('rateLimitTime', 'Rate limit time'),
        CatalogGridItemColumn.def('rateLimitWS', 'Rate limit WS')
      ])
    }
    this.columns = this.columns.concat([
      CatalogGridItemColumn.def('closed', 'Estado'),
      CatalogGridItemColumn.def('firebaseUUID', 'UUID de Firebase'),
      CatalogGridItemColumn.def('creationDateString', 'Fecha de creación'),
      CatalogGridItemColumn.def('updateDateString', 'Fecha de actualización')
    ])
  }

  onSaveDomainFeedback: boolean = false

  created () {
    this.searchData()
    this.loadClientsPage(new PageRequest(this.PAGE_SIZE))
  }

  @Watch('selectedItem')
  changeDomainWorkspaces (): void {
    this.domainWorkspaces = this.workspaces[this.selectedItem.id]
  }

  /* User permissions */

  /**
   * Validates if the user has a specific role
   *
   * @param role to check
   */
  isUserRole (role: string) {
    return userInfoStore.userInfo.role.name === role
  }

  /** @returns if the user has financesControl or financesAdmin role */
  get isFinancesRole () {
    return this.isUserRole(RoleNames.FINANCES_ADMIN) || this.isUserRole(RoleNames.FINANCES_CONTROL)
  }

  /** @return if the user has operationsAdmin role */
  get isOperationRole () {
    return this.isUserRole(RoleNames.OPERATIONS_ADMIN)
  }

  get createPermission () {
    return userInfoStore.userInfo.role.permissions.some(it => it.name === PermissionNames.DOMAINS_CREATE)
  }

  get updatePermission () {
    return userInfoStore.userInfo.role.permissions.some(it => it.name === PermissionNames.DOMAINS_UPDATE)
  }

  get openClosePermission () {
    return userInfoStore.inRootDomain() &&
      userInfoStore.userInfo.role.permissions.some(it => it.name === PermissionNames.DOMAINS_OPEN_CLOSE)
  }

  get openCloseLabel () {
    if (this.selectedItem.id === undefined) return 'Abrir/Cerrar'
    if (this.selectedItem.closed) {
      return 'Abrir'
    } else {
      return 'Cerrar'
    }
  }

  get workspacesEditPermission () {
    return userInfoStore.userInfo.role.permissions.some(it => it.name === PermissionNames.WORKSPACES_UPDATE)
  }

  /** Import clients */
  get importDomains () {
    return userInfoStore.userInfo.role.permissions.some(it => it.name === PermissionNames.DOMAINS_IMPORT)
  }

  /* Load data */

  searchData () {
    if (this.loadingData) return
    this.items = []
    this.loadingData = true
    this.cleanFilter()
    this.loadPage(new PageRequest<DomainCatalogFilters>(this.PAGE_SIZE))
  }

  async loadPage (pageRequest: PageRequest<DomainCatalogFilters>) {
    pageRequest.sort = 'name'
    const response = await catalogsService.getDomains(pageRequest) as any
    this.items.push(...response.results.map((it: any) =>
      this.fillGridItem(it, new DomainCatalogGridItem())
    ))

    if (this.items.length < response.total) {
      pageRequest.page++
      await this.loadPage(pageRequest)
    } else {
      this.loadWorkspacesPage()
      this.filteredItems = this.items
    }
  }

  async loadClientsPage (pageRequest: PageRequest<any>) {
    const response = await catalogsService.getClients(pageRequest) as any
    this.clients.push(...response.results.map((it: Client) => {
      return { id: it.id, code: it.clientCode, name: it.name, status: it.status }
    }))

    if (this.clients.length < response.total) {
      pageRequest.page++
      await this.loadClientsPage(pageRequest)
    } else {
      this.loadingData = false
    }
  }

  async loadWorkspacesPage () {
    this.workspaces = {}
    const allWorkspaces = await catalogsService.getWorkspacesAll()
    this.items.forEach((domain) => {
      const allWorkspacesForDomain = allWorkspaces
        .filter((workspace) => workspace.domain.id === domain.id)
        .map((workspace) => ({
          clientName: workspace.client.name,
          clientCode: workspace.client.clientCode,
          owner: workspace.owner,
          status: workspace.status,
          creationDate: moment(workspace.creationDate).format('DD/MM/YYYY')
        }))
      this.workspaces[domain.id] = allWorkspacesForDomain
      // Get owner workspace info
      const ownerWorkspace = allWorkspacesForDomain.find((workspace) => workspace.owner)
      if (ownerWorkspace) {
        domain.status = Workspace.getStatusDescription(ownerWorkspace.status)
        domain.ownerDomain = `${ownerWorkspace.clientCode} ${ownerWorkspace.clientName} (Desde el ${ownerWorkspace.creationDate})`
      }
    })
  }

  /* Grid and action events */

  selectItem (item: any) {
    switch (this.currentAction) {
      case CrudActions.READ:
      case CrudActions.READ_DETAIL:
      case CrudActions.UPDATE:
      case CrudActions.WORKSPACES:
        this.selectedItem = item
        break
      case CrudActions.CREATE:
        this.selectedItem = this.updatePermission ? item : this.selectedItem
        this.currentAction = CrudActions.UPDATE
        break
    }
  }

  selectItemAndShowDetail (item: any) {
    this.selectItem(item)
    this.queryItem()
  }

  isItemSelected (item: any) {
    return this.selectedItem === item
  }

  cleanItemSelected () {
    this.selectedItem = new DomainCatalogGridItem()
  }

  queryItem () {
    if (this.selectedItem.id) {
      this.currentAction = CrudActions.READ_DETAIL
    }
  }

  newItem () {
    this.selectedItem = new DomainCatalogGridItem()
    this.currentAction = CrudActions.CREATE
  }

  updateItem () {
    if (this.selectedItem.id) {
      this.currentAction = CrudActions.UPDATE
    }
  }

  showWorkspacesAdmin () {
    if (this.selectedItem.id) {
      this.currentAction = CrudActions.WORKSPACES
    }
  }

  fillGridItem (origin: Domain, target: DomainCatalogGridItem) {
    Object.assign(target, origin)
    target.creationDateString = origin.creationDate ? new DateFormat(origin.creationDate).toDateString() : '- - / - - / - - - -'
    target.updateDateString = origin.updateDate ? new DateFormat(origin.updateDate).toDateString() : '- - / - - / - - - -'
    target.limitLicenses = (origin.limitLicenses ? 'Sí' : 'No') as any
    target.usePredictiveDialerOpt = (origin.usePredictiveDialerOpt ? 'Sí' : 'No') as any
    target.domainTypeName = Domain.getDomainTypeDescription(origin.domainType)
    target.fullTextSearchType = Domain.getDomainFullTextSearchTypeDescription(origin.fullTextSearchType)
    target.securityLevelName = Domain.getDomainSecurityLevelDescription(origin.securityLevel)
    target.closed = origin.closed
    return target
  }

  /* Filter and sorting data */

  cleanFilter () {
    this.resetPaginator()
    this.filters = new DomainCatalogFilters()
    this.filteredItems = this.items
  }

  filterItems () {
    const regexSearch = new RegExp(this.filters.search, 'i')
    this.resetPaginator()
    let creationDateString = this.filters.creation ? moment(this.filters.creation).format('DD/MM/YYYY') : ''
    this.filteredItems = this.items.filter((it: DomainCatalogGridItem, index: number) =>
      (!this.filters.search || regexSearch.test(it.name) || regexSearch.test(it.ownerDomain)) &&
      (!this.filters.domainType.length || this.filters.domainType.includes(it.domainType)) &&
      (!creationDateString || (it.ownerDomain && it.ownerDomain.indexOf(creationDateString) > -1))
    )
    this.cleanItemSelected()
  }

  isRowVisibleInPage (rowIndex: number) {
    return rowIndex >= this.itemsVisibleRange[0] && rowIndex < this.itemsVisibleRange[1]
  }

  sortItems (col: CatalogGridItemColumn) {
    catalogsService.sortItems(this.filteredItems, col)
  }

  onPageChange (page: number) {
    this.itemsVisibleRange = [(page - 1) * this.PAGE_SIZE_VISIBLE_ITEMS, (page - 1) * this.PAGE_SIZE_VISIBLE_ITEMS + this.PAGE_SIZE_VISIBLE_ITEMS]
    this.cleanItemSelected()
  }

  resetPaginator () {
    this.itemsVisibleRange = [0, this.PAGE_SIZE_VISIBLE_ITEMS]
    this.paginatorKey = new Date().getTime()
    this.cleanItemSelected()
  }

  /* Form */

  onCancelForm () {
    this.currentAction = CrudActions.READ
  }

  onItemSaved (item: Domain) {
    let isNewItem = false
    if (!this.selectedItem.id) {
      isNewItem = true
      this.selectedItem = new DomainCatalogGridItem()
      this.selectedItem.id = item.id
      this.items.unshift(this.selectedItem)
    }
    this.fillGridItem(item, this.selectedItem)
    this.currentAction = CrudActions.READ
    this.showOnSaveFeedback()

    if (isNewItem && this.workspacesEditPermission) {
      setTimeout(() => {
        this.currentAction = CrudActions.WORKSPACES
      }, 700)
    }
  }

  showOnSaveFeedback () {
    this.onSaveDomainFeedback = true
    setTimeout(() => {
      this.hideFeedback()
    }, 4500)
  }

  onItemWorkspacesLoaded (ws: Workspace[]) {
    if (ws && ws.length) {
      let clientsForDomainFormatted: string[] = []
      ws.forEach((element: Workspace) => {
        clientsForDomainFormatted.push(`${element.client.clientCode} ${element.client.name} (Desde el ${moment(element.creationDate).format('DD/MM/YYYY')})`)
        if (element.owner) {
          Vue.set(this.selectedItem, 'status', Workspace.getStatusDescription(element.status))
        }
      })
      Vue.set(this.selectedItem, 'workspacesInfo', clientsForDomainFormatted.join(', '))
    }
  }

  /** Import file */

  /**
   * Creates a domain instance based on a object with domain values
   * @param domain - a object with domain values to use
   * @returns newClient - a domain instance from Catalogs format
   */
  async prepareDomain (domain: any) {
    try {
      let newDomain = Domain.getIntanceForEdit()
      newDomain.name = domain.hasOwnProperty('name') ? domain['name'] : ''
      newDomain.securityLevel = domain.hasOwnProperty('securityLevel') ? domain['securityLevel'] : ''
      newDomain.minimumRevision = domain.hasOwnProperty('minimumRevision') ? domain['minimumRevision'] : null
      newDomain.webBMFMinimumRevision = domain.hasOwnProperty('webBMFMinimumRevision') ? domain['webBMFMinimumRevision'] : null
      newDomain.broadcast = domain['broadcast']
      newDomain.usePredictiveDialerOpt = domain.hasOwnProperty('usePredictiveDialerOpt') ? domain['usePredictiveDialerOpt'] : null
      newDomain.limitLicenses = domain.hasOwnProperty('limitLicenses') ? domain['limitLicenses'] : null
      newDomain.mapProvider = domain.hasOwnProperty('mapProvider') ? domain['mapProvider'] : null
      newDomain.fullTextSearchType = domain.hasOwnProperty('fullTextSearchType') ? domain['fullTextSearchType'] : null
      newDomain.rateLimitTime = domain.hasOwnProperty('rateLimitTime') ? domain['rateLimitTime'] : null
      newDomain.rateLimitWS = domain.hasOwnProperty('rateLimitWS') ? domain['rateLimitWS'] : null
      newDomain.trackingMode = domain.hasOwnProperty('trackingMode') ? domain['trackingMode'] : null
      newDomain.productLine = domain.hasOwnProperty('productLine') ? domain['productLine'] : null
      newDomain.businessSegment = domain.hasOwnProperty('businessSegment') ? domain['businessSegment'] : null
      newDomain.domainType = domain.hasOwnProperty('domainType') ? domain['domainType'] : null
      newDomain.billingCode = domain.hasOwnProperty('billingCode') ? domain['billingCode'] : null
      if (newDomain.validateRequiredValues().length) {
        throw ErrorImport.getErrorRequiredValues(ImportationErrorAdvices.no_required_domain_values)
      } else {
        let formatErrors = newDomain.validateFormatValues()
        if (formatErrors.length) {
          throw ErrorImport.getErrorFormatValues(
            formatErrors,
            domain.name
          )
        }
      }
      return newDomain
    } catch (error) {
      LogService.error('Error durante la preparación de un dominio.')
      throw error
    }
  }

  /**
   * Gets the domain by id from 'domain' id,
   * and update its values based on same domain parameter
   * @param domain - values to update the domain with id 'domain.id'
   * @returns a domain object updated
   */
  async updatedDomain (domain: any) {
    try {
      let domainEntity = new Domain()
      let currentDomain: Domain = await catalogsService.getDomainById(domain.id)
        .catch((error) => {
          LogService.error(`Error en la consulta del dominio: ${ToString(error.response ?? error)}`)
          throw new ErrorImport(
            ImportErrorCode.NO_RELATED_ENTITY,
            ErrorTitles.domain_not_found,
            `El dominio con el ID: ${domain.id} no se ha podido cargar.`,
            ImportationErrorAdvices.entity_not_found
          )
        })
      Object.assign(domainEntity, currentDomain, domain)
      let missingRequiredValues = domainEntity.validateRequiredValues()
      if (missingRequiredValues.length) {
        throw ErrorImport.getErrorRequiredValues([
          `Lo siguientes valores son necesarios para poder actualizar: ${missingRequiredValues},
          verifica que la columna esta presente y/o que cuenta con un valor. Dominio: ${domainEntity.id} . ${domainEntity.name}`
        ])
      } else {
        let errorFormatValues = domainEntity.validateFormatValues()
        if (errorFormatValues.length) {
          throw ErrorImport.getErrorFormatValues(
            errorFormatValues,
            `Dominio: ${domainEntity.id} - ${domainEntity.name}`
          )
        }
      }
      return domainEntity
    } catch (error) {
      LogService.error('Error durante la actualización de valores de un dominio.')
      throw error
    }
  }

  hideFeedback (): void {
    this.onSaveDomainFeedback = false
  }
  /**
   * Obtains the values to display as details on import file action.
   * This values must be the same specified on 'importDomainFields' variable
   * @returns object with column values to use on import file action
   */
  domainDetails (domain: any) {
    return {
      id: domain.id,
      name: domain.name
    }
  }

  /**
   * Gets the save client funtion from catalogService to use in import component
   */
  get saveDomain () {
    return catalogsService.saveDomain
  }

  showOpenCloseDomainModal () {
    this.$bvModal.show('open-close-domain-modal')
  }

  hideOpenCloseDomainModal () {
    this.$bvModal.hide('open-close-domain-modal')
  }

  async handleOpenCloseDomain () {
    this.openCloseDomainLoading = true
    try {
      if (!this.selectedItem.closed) {
        const releaseLicensesResponse = await catalogsService.releaseDomainLicenses(this.selectedItem.id)
        this.$bvToast.toast(`Se han liberado ${releaseLicensesResponse.releasedLicenses} ${releaseLicensesResponse.releasedLicenses === 1 ? 'licencia' : 'licencias'}.`, {
          title: 'Licencias liberadas',
          variant: 'success',
          solid: true
        })
      }

      await catalogsService.closeDomain(this.selectedItem.id, !this.selectedItem.closed)
      this.selectedItem.closed = !this.selectedItem.closed
      this.$bvToast.toast(`El dominio se ha ${this.selectedItem.closed ? 'cerrado' : 'abierto'} correctamente.`, {
        title: 'Operación exitosa',
        variant: 'success',
        solid: true
      })
      this.hideOpenCloseDomainModal()
    } catch (error) {
      LogService.error(`Error al intentar ${this.openCloseLabel.toLowerCase()} el dominio: ${ToString(error)}`)
      this.$bvToast.toast(`No se ha podido ${this.openCloseLabel.toLowerCase()} el dominio. Inténtelo nuevamente más tarde.`, {
        title: 'Error',
        variant: 'danger',
        solid: true
      })
    } finally {
      this.openCloseDomainLoading = false
    }
  }

  /* Export data */

  printCsv () {
    let csv = this.columns.map((col) => { return reportsService.formatTextForCsvFile(col.name) }).join(',') + '\n'

    for (let it of this.filteredItems) {
      csv += `${reportsService.formatTextForCsvFile(it.name)},`
      csv += `${reportsService.formatNullableTextForCsvFile(it.id)},`
      csv += `${reportsService.formatTextForCsvFile(it.status)},`
      csv += `${reportsService.formatTextForCsvFile(it.ownerDomain)},`

      if (this.isFinancesRole) {
        csv += `${reportsService.formatTextForCsvFile(it.domainTypeName)},`
        csv += `${reportsService.formatTextForCsvFile(it.productLine)},`
        csv += `${reportsService.formatTextForCsvFile(it.businessSegment)},`
        csv += `${reportsService.formatTextForCsvFile(it.billingCode)},`
      } else if (this.isOperationRole) {
        csv += `${reportsService.formatTextForCsvFile(it.securityLevelName)},`
        csv += `${reportsService.formatNullableTextForCsvFile(it.webBMFMinimumRevision)},`
        csv += `${reportsService.formatTextForCsvFile(it.broadcast)},`
        csv += `${reportsService.formatTextForCsvFile(it.usePredictiveDialerOpt as any)},`
        csv += `${reportsService.formatTextForCsvFile(it.limitLicenses as any)},`
        csv += `${reportsService.formatTextForCsvFile(it.mapProvider)},`
        csv += `${reportsService.formatTextForCsvFile(it.fullTextSearchType)},`
        csv += `${reportsService.formatTextForCsvFile(it.trackingMode)},`
        csv += `${reportsService.formatNullableTextForCsvFile(it.rateLimitTime)},`
        csv += `${reportsService.formatNullableTextForCsvFile(it.rateLimitWS)},`
      }
      csv += `${reportsService.formatNullableTextForCsvFile(it.firebaseUUID)},`
      csv += `${it.creationDateString},`
      csv += `${it.updateDateString}\n`
    }

    let element = this.$refs.csvDownload as any
    element.href = 'data:text/csv;charset=utf-8,' + encodeURI(csv)
    element.target = '_blank'
    element.download = `dominios.csv`
    element.click()
  }
}
